import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0.00';
  return '$' + Math.abs(Number(val)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const TYPE_CONFIG = {
  commission: { labelKey: 'payments.type.commission', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  payment: { labelKey: 'payments.type.payment', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  adjustment: { labelKey: 'payments.type.adjustment', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  tournament: { labelKey: 'payments.type.tournament', color: '#ec4899', bg: 'rgba(236,72,153,0.15)' },
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ManagerPaymentsView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth + fetch
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data: userData, error: userErr } = await supabase
          .from('users').select('id, name, email').eq('id', session.user.id).single();
        if (userErr) { console.error('User fetch error:', userErr); return; }
        setUserInfo(userData);

        if (userData?.id) {
          const { data, error: txErr } = await supabase
            .from('manager_transactions')
            .select('*')
            .eq('manager_id', userData.id)
            .order('created_at', { ascending: false });
          if (txErr) console.error('Transactions fetch error:', txErr);
          setTransactions(data || []);
        }
      } catch (err) {
        console.error('Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // KPIs
  const kpis = useMemo(() => {
    let commissions = 0, payments = 0, adjustments = 0, tournaments = 0;

    transactions.forEach(tx => {
      const amt = Number(tx.amount) || 0;
      if (tx.type === 'commission') commissions += amt;
      else if (tx.type === 'payment') payments += Math.abs(amt);
      else if (tx.type === 'adjustment') adjustments += amt;
      else if (tx.type === 'tournament') tournaments += amt;
    });

    return {
      solde: commissions + adjustments + tournaments - payments,
      commissions,
      payments,
      adjustments,
      tournaments,
    };
  }, [transactions]);

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      <main className="flex-1 overflow-y-auto pb-20 md:pb-0" style={{ backgroundColor: 'var(--color-background)' }}>
        <ManagerMobileHeader title={t('payments.title')} />

        <div className="p-4 sm:p-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('payments.title')}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('payments.subtitle')}
            </p>
          </div>

          {/* 5 KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {[
              { label: t('payments.kpi.balanceDue'), value: kpis.solde, color: kpis.solde >= 0 ? '#22c55e' : '#ef4444', prefix: kpis.solde < 0 ? '-' : '' },
              { label: t('payments.kpi.commissions'), value: kpis.commissions, color: '#22c55e' },
              { label: t('payments.kpi.adjustments'), value: kpis.adjustments, color: '#f59e0b', prefix: kpis.adjustments < 0 ? '-' : kpis.adjustments > 0 ? '+' : '' },
              { label: t('payments.kpi.tournaments'), value: kpis.tournaments, color: '#ec4899', prefix: '-' },
              { label: t('payments.kpi.paid'), value: kpis.payments, color: '#a855f7' },
            ].map((kpi, i) => (
              <div key={i} className="rounded-xl p-4 border" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
                <span className="text-xs font-medium block mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{kpi.label}</span>
                <p className="text-lg sm:text-xl font-bold" style={{ color: kpi.color, fontFamily: 'Sora, sans-serif' }}>
                  {kpi.prefix || ''}{formatCurrency(kpi.value)}
                </p>
              </div>
            ))}
          </div>

          {/* Historique */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
            <div className="px-4 sm:px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
              <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('payments.transactionHistory')}
              </h3>
            </div>

            {loading ? (
              <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('common.loading')}</div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('payments.noTransaction')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[{ key: 'date', label: t('payments.tableDate') }, { key: 'type', label: t('payments.tableType') }, { key: 'detail', label: t('payments.tableDetail') }, { key: 'amount', label: t('payments.tableAmount') }].map(h => (
                        <th key={h.key}
                          className={`px-4 sm:px-6 py-3 text-xs font-medium ${h.key === 'amount' ? 'text-right' : 'text-left'}`}
                          style={{ color: 'rgba(255,255,255,0.35)' }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => {
                      const amt = Number(tx.amount) || 0;
                      const typeConf = TYPE_CONFIG[tx.type] || TYPE_CONFIG.adjustment;
                      const isNeg = amt < 0;
                      return (
                        <tr key={tx.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td className="px-4 sm:px-6 py-3.5 text-sm text-white/70">{formatDate(tx.created_at)}</td>
                          <td className="px-4 sm:px-6 py-3.5">
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: typeConf.bg, color: typeConf.color }}>
                              {t(typeConf.labelKey)}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-3.5 text-sm text-white/80">{tx.description || '-'}</td>
                          <td className="px-4 sm:px-6 py-3.5 text-sm font-semibold text-right"
                            style={{ color: isNeg ? '#ef4444' : '#22c55e' }}>
                            {isNeg ? '- ' : '+ '}{formatCurrency(amt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <ManagerBottomNav />
    </div>
  );
};

export default ManagerPaymentsView;
